import { inject, Injectable } from '@angular/core';
import { CadFile } from '../../models/cad-file.model';
import { CadObject } from '../../models/cad-object.model';
import {
  MouldSolution,
  MouldSolutionsResponseSchema,
} from '../../models/mould-solution.model';
import {
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { DownloadService } from './download.service';

@Injectable({ providedIn: 'root' })
export class CadFileService {
  private http: HttpClient = inject(HttpClient);
  private downloadService = inject(DownloadService);

  postCadFiles(
    files: File[],
    options: { forMoulding?: boolean } = {},
  ): Observable<CadFile[]> {
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file) {
        formData.append('file', file, file.name);
      }
    }
    if (options.forMoulding !== undefined) {
      formData.append('for_moulding', options.forMoulding.toString());
    }

    return this.http.post<CadFile[]>('/quotes-api/cad-files/', formData);
  }

  // Fetches each object by ID, then loads its geometry via
  // `loadCadObjectGeometry`. Emits the flat list once; errors on the first
  // failed request.
  loadCadObjects(
    cadObjectIds: readonly number[],
    options: { onObjectLoaded?: (cadObject: CadObject) => void } = {},
  ): Observable<CadObject[]> {
    // forkJoin([]) never emits, so handle empty input explicitly.
    if (!cadObjectIds.length) return of([]);

    return forkJoin(cadObjectIds.map((id) => this.getCadObject(id))).pipe(
      switchMap((cadObjects) =>
        this.loadCadObjectGeometry(cadObjects, options),
      ),
    );
  }

  // Downloads PLY geometry for the given objects and their descendants,
  // storing it on `cadObject.plyBlob` in place. Emits the flat list (parent
  // before children) once. The first failed download errors the call and
  // cancels the rest; objects that already have geometry, or have no PLY URL,
  // are skipped. `onObjectLoaded` fires per object for progress.
  loadCadObjectGeometry(
    cadObjects: readonly CadObject[],
    options: { onObjectLoaded?: (cadObject: CadObject) => void } = {},
  ): Observable<CadObject[]> {
    const flattened = this.flattenCadObjects(cadObjects);
    if (!flattened.length) return of([]);

    return forkJoin(
      flattened.map((cadObject) =>
        this.loadPlyBlob(cadObject).pipe(
          tap(() => options.onObjectLoaded?.(cadObject)),
        ),
      ),
    ).pipe(map(() => flattened));
  }

  // Stores the object's PLY on `cadObject.plyBlob`, preferring the
  // disconnected file. Does nothing if it already has geometry or no PLY URL.
  private loadPlyBlob(cadObject: CadObject): Observable<CadObject> {
    const plyUrl =
      cadObject.ply_disconnected_url || cadObject.ply_connected_url;
    if (cadObject.plyBlob || !plyUrl) return of(cadObject);

    return this.http.get(plyUrl, { responseType: 'blob' }).pipe(
      map((plyBlob) => {
        cadObject.plyBlob = plyBlob;
        return cadObject;
      }),
    );
  }

  // [A, B(children: [C])] -> [A, B, C]. `children` can be null.
  flattenCadObjects(cadObjects: readonly CadObject[]): CadObject[] {
    return cadObjects.flatMap((cadObject) => [
      cadObject,
      ...this.flattenCadObjects(cadObject.children ?? []),
    ]);
  }

  getCadFile(id: number): Observable<CadFile> {
    return this.http.get<CadFile>(`/quotes-api/cad-file/${id}/`);
  }

  async downloadCadFile(id: number): Promise<void> {
    const cadFile = await firstValueFrom(this.getCadFile(id));
    await this.downloadService.downloadFile(cadFile.file, {
      defaultFilename: cadFile.filename,
      mimeType: 'application/octet-stream',
    });
  }

  getCadObject(cadObjectId: number): Observable<CadObject> {
    return this.http.get<CadObject>(`/quotes-api/cad-object/${cadObjectId}/`);
  }

  getMouldSolutions(cadObjectId: number): Observable<MouldSolution[]> {
    return this.http
      .get<unknown>(`/quotes-api/cad-object/${cadObjectId}/mould-solutions/`)
      .pipe(map((response) => MouldSolutionsResponseSchema.parse(response)));
  }
}
